import {
  createCommunityApi,
  createBaseApi,
  createMarketApi,
  createUserApi,
  BaseApi,
  CommunityApi,
  MarketApi,
  UserApi,
} from './routes';
import { createActivityRewardsApi, type ActivityRewardsApi } from './routes/activity-rewards';
import createPointsApi, { PointsApi } from './routes/points';
import createReferralApi, { ReferralApi } from './routes/referral';
import createTaskApi, { TaskApi } from './routes/task';
import createSSEApi, { SSEApi } from './sse';
import { Options } from 'ky';

export type ApiOptions = Options & {
  apiKey?: string;
  baseTokenAddress?: string;
};

export type ApiRouter = {
  base: BaseApi;
  community: CommunityApi;
  market: MarketApi;
  user: UserApi;
};

export type ApiInstance = {
  options: ApiOptions;
  base: BaseApi;
  community: CommunityApi;
  market: MarketApi;
  user: UserApi;
  task: TaskApi;
  points: PointsApi;
  referral: ReferralApi;
  activityRewards: ActivityRewardsApi;
  sse: SSEApi;
  updateOptions: (newOptions: Partial<ApiOptions>) => void;
};

export const mergeOptions = (options: ApiOptions): ApiOptions => {
  const { apiKey, hooks = {}, baseTokenAddress } = options;
  return {
    ...options,
    hooks: {
      ...hooks,
      beforeRequest: [
        (request: Request) => {
          if (apiKey) request.headers.set('Authorization', `Bearer ${apiKey}`);
          if (baseTokenAddress) request.headers.set('BaseTokenAddress', baseTokenAddress);
        },
        ...(hooks.beforeRequest || []),
      ],
    },
  };
};

export const createApi = (initialOptions: ApiOptions): ApiInstance => {
  let options: ApiOptions = mergeOptions(initialOptions);
  const instance: ApiInstance = {
    options,
    base: createBaseApi(options),
    community: createCommunityApi(options),
    market: createMarketApi(options),
    user: createUserApi(options),
    task: createTaskApi(options),
    points: createPointsApi(options),
    referral: createReferralApi(options),
    activityRewards: createActivityRewardsApi(options),
    sse: createSSEApi(options),
    updateOptions: (newOptions: Partial<ApiOptions>) => {
      options = mergeOptions({ ...options, ...newOptions });
      instance.options = options;
      instance.base = createBaseApi(options);
      instance.community = createCommunityApi(options);
      instance.market = createMarketApi(options);
      instance.user = createUserApi(options);
      instance.points = createPointsApi(options);
      instance.referral = createReferralApi(options);
      instance.activityRewards = createActivityRewardsApi(options);
    },
  };
  return instance;
};
